import CapacityMark from './CapacityMark';
import { PRODUCT_BADGE_SOLD_OUT_LABEL } from '../config';
import {
  findSelectedSibling,
  resolveActiveVariant,
} from '../utils/ingredientFamilies';
import { displayIngredientName } from '../utils/displayIngredientName';

const CAFFEINE_SHORT = {
  none: 'Caffeine free',
  low: 'Low caffeine',
  medium: 'Moderate caffeine',
  high: 'High caffeine',
};

function capitalizeSentenceStart(text) {
  if (!text || typeof text !== 'string') return '';
  const t = text.trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function IngredientFamilyRow({
  family,
  selectedIngredients,
  preferredQuality,
  onPreferredQuality,
  onToggle,
  onQualityChange,
  onOpenModal,
  isSynergy,
}) {
  const preferred = preferredQuality || 'standard';
  const active = resolveActiveVariant(family, selectedIngredients, preferred);
  const selectedSibling = findSelectedSibling(family, selectedIngredients);
  const isSelected = Boolean(selectedSibling);
  const isOutOfStock = !active.in_stock;
  const isRunningLow = active.in_stock && active.is_running_low;
  const isOrganic = active.quality === 'organic';
  const organicVariant = family.variants.find((v) => v.quality === 'organic');
  const standardVariant = family.variants.find((v) => v.quality === 'standard');
  const rowName = displayIngredientName(family.name, active.quality);

  const handleToggle = () => {
    if (selectedSibling) {
      onToggle(selectedSibling.id);
      return;
    }
    if (!active.in_stock) return;
    onToggle(active.id);
  };

  const handleQualityPick = (variant) => {
    if (!variant.in_stock) return;
    onPreferredQuality(family.familyKey, variant.quality);
    if (selectedSibling && selectedSibling.id !== variant.id) {
      onQualityChange(selectedSibling.id, variant.id);
    }
  };

  const organicPillDisabled =
    family.hasQualityChoice &&
    organicVariant &&
    !isOrganic &&
    !organicVariant.in_stock;

  return (
    <div
      data-ingredient-family={family.familyKey}
      className={`ingredient-row${isSelected ? ' selected' : ''}${
        isSynergy && !isSelected ? ' ingredient-row--synergy' : ''
      }${isOutOfStock ? ' out-of-stock' : ''}`}
    >
      <button
        type="button"
        className={`ingredient-row__select${isSelected ? ' selected' : ''}`}
        onClick={handleToggle}
        aria-label={isSelected ? `Remove ${rowName}` : `Add ${rowName}`}
        disabled={isOutOfStock && !isSelected}
      >
        <svg
          className="ingredient-row__check"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>

      <div
        className="ingredient-row__body"
        onClick={() => onOpenModal(active)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpenModal(active)}
      >
        <div className="ingredient-row__name-line">
          <span className="ingredient-row__name">{rowName}</span>
          <CapacityMark tier={active.capacity_tier} />
          {isSynergy && !isSelected && (
            <span className="ingredient-row__pairs-mark">pairs well</span>
          )}
          {isOutOfStock && (
            <span className="product-badge-sold-out">{PRODUCT_BADGE_SOLD_OUT_LABEL}</span>
          )}
          {!isOutOfStock && isRunningLow && (
            <span className="running-low-badge">Low stock</span>
          )}
        </div>
        {family.hasQualityChoice && organicVariant && standardVariant && (
          <div
            className="ingredient-row__quality"
            role="group"
            aria-label={`${family.name} quality`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`quality-pill${active.quality === 'standard' ? ' quality-pill--on' : ''}`}
              disabled={!standardVariant.in_stock && active.quality !== 'standard'}
              onClick={() => handleQualityPick(standardVariant)}
              aria-pressed={active.quality === 'standard'}
            >
              Standard
            </button>
            <button
              type="button"
              className={`quality-pill${isOrganic ? ' quality-pill--on' : ''}`}
              disabled={organicPillDisabled}
              onClick={() => handleQualityPick(organicVariant)}
              aria-pressed={isOrganic}
            >
              Organic
            </button>
          </div>
        )}
        {!family.hasQualityChoice && isOrganic && (
          <div className="ingredient-row__quality">
            <span className="quality-pill quality-pill--on quality-pill--static">
              Organic
            </span>
          </div>
        )}
        {active.caffeine !== 'none' && (
          <div className="ingredient-row__tags">
            <span className={`tag caffeine-tag caffeine-${active.caffeine}`}>
              {CAFFEINE_SHORT[active.caffeine] || active.caffeine}
            </span>
          </div>
        )}
        {active.benefits?.[0] && (
          <p className="ingredient-row__meta">
            {capitalizeSentenceStart(active.benefits[0])}
          </p>
        )}
      </div>
    </div>
  );
}

export default IngredientFamilyRow;
